import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import type { Debt, IncomeSource, MonthlyLedgerEntry } from '@/types'
import { getLedgerRepository } from '@/repository'
import { PaymentRpcUnavailableError } from '@/repository/ledgerTypes'
import { formatCurrencyExact } from '@/lib/formatters'
import { hasPendingWrites, writeEpoch } from './writeTracker'
import { safeUUID } from '@/lib/uuid'
import { useDebtStore } from './debtStore'
import {
  addMonths,
  buildBlankLedgerEntry,
  buildNextMonthFromPrevious,
  closeMonthEntry,
  applyMinimumsOnly,
  applyStrategySurplus,
  currentMonth,
} from '@/engine/ledger'
import { effectiveAPR } from '@/engine/promoWatcher'
import { PAID_OFF_TOLERANCE } from '@/engine/tolerances'
import { withTimeout } from '@/lib/withTimeout'
import { parseLocalMonth } from '@/lib/parseLocalDate'

/**
 * Persist the active-month selection across page loads / PWA cold starts.
 * Without this, every fresh load resets to today's calendar month — which
 * means a user navigating around the app then refreshing the browser would
 * be yanked back to today instead of where they were last working.
 *
 * Stored as a plain "YYYY-MM" string under a versioned key so any future
 * migration to a workspace-keyed scheme can move forward cleanly. Failures
 * (private browsing, quota issues) are silently absorbed — falling back to
 * today is the safe degraded behavior.
 */
const ACTIVE_MONTH_STORAGE_KEY = 'debtos:activeMonth:v1'

function readPersistedActiveMonth(): string | null {
  try {
    const raw = localStorage.getItem(ACTIVE_MONTH_STORAGE_KEY)
    if (!raw) return null
    // Defensive shape check: must be "YYYY-MM"
    return /^\d{4}-\d{2}$/.test(raw) ? raw : null
  } catch {
    return null
  }
}

function writePersistedActiveMonth(month: string): void {
  try {
    localStorage.setItem(ACTIVE_MONTH_STORAGE_KEY, month)
  } catch {
    // Storage unavailable / quota — non-fatal, just lose the persistence.
  }
}

interface LedgerState {
  incomeSources: IncomeSource[]
  ledgerEntries: MonthlyLedgerEntry[]
  /** Which month the user is currently viewing in the UI */
  activeMonth: string
  isLoading: boolean
  isInitialized: boolean
  /** Surfaced when init fails so the UI can show a real error instead of "Loading..." forever */
  initError: string | null
  /**
   * Surfaced when a background (optimistic) cloud write fails. The in-memory
   * edit is kept so the user doesn't lose their change; this lets the UI warn
   * that it hasn't synced. Cleared on the next successful edit or on dismiss.
   */
  writeError: string | null
  /**
   * v0.8.56 — set when another household member changed a payment before
   * this device's save landed (atomic `log_payment` conflict) or when an
   * additive payment was stacked on top of theirs. Informational banner.
   */
  syncNotice: string | null
}

/** Prefix on `writeError` when a payment save failed and was rolled back. */
export const PAYMENT_NOT_SAVED = 'Payment not saved'

/** v0.8.56 — what happened to a payment save. */
export interface PaymentOutcome {
  status: 'ok' | 'conflict' | 'error'
  /** The payment as stored after the call (conflict: the other device's value). */
  actualPayment?: number
}

interface LedgerActions {
  initialize: (debts: Debt[]) => Promise<void>

  // Income source CRUD
  addIncomeSource: (source: Omit<IncomeSource, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateIncomeSource: (
    id: string,
    patch: Partial<Omit<IncomeSource, 'id' | 'createdAt'>>
  ) => Promise<void>
  removeIncomeSource: (id: string) => Promise<void>

  // Active month navigation
  setActiveMonth: (month: string) => void

  /** Dismiss the background-write error banner. */
  clearWriteError: () => void

  // Ledger entry editing (current month only — closed months are immutable)
  setIncomeAmount: (sourceId: string, amount: number) => Promise<void>
  /** Sets the explicit per-month minimum override for planning entries */
  setMonthMinimumPayment: (debtId: string, amount: number) => Promise<void>
  /** Clears the override so the registry default applies again */
  clearMonthMinimumPaymentOverride: (debtId: string) => Promise<void>
  setPlannedPayment: (debtId: string, amount: number) => Promise<void>
  setActualPayment: (debtId: string, amount: number | undefined) => Promise<PaymentOutcome>
  /**
   * v0.8.56 — "I paid $X more" (Log a Payment dialog). Adds on top of the
   * stored total; if another device logged first, retries once on top of
   * the fresh value so both payments count.
   */
  addPayment: (debtId: string, amount: number) => Promise<PaymentOutcome>
  clearSyncNotice: () => void
  /**
   * v0.8.56 — background re-read (refresh-on-focus) so a household
   * member's changes appear. Discarded if any write started meanwhile.
   */
  refresh: () => Promise<void>
  setEndingBalanceOverride: (debtId: string, amount: number | undefined) => Promise<void>

  // Quick-fill actions
  applyMinimums: (debts: Debt[]) => Promise<void>
  applyAvalancheSurplus: (debts: Debt[], surplus: number) => Promise<void>
  applySnowballSurplus: (debts: Debt[], surplus: number) => Promise<void>

  // Reset the active planning month from current registry state — wipes
  // allocations and rebuilds from current debts / incomeSources, preserves
  // income amounts entered + notes. Essentials are derived live from
  // recurring expenses so there's nothing to preserve here.
  resetMonthFromRegistry: (debts: Debt[]) => Promise<void>

  // Month lifecycle
  // `essentials` is the resolved essential-expenses value at close-time;
  // gets snapshotted into the closed entry so historical records are immutable.
  closeCurrentMonth: (debts: Debt[], essentials: number) => Promise<void>
  startNextMonth: (debts: Debt[]) => Promise<void>

  /**
   * Mark the active month as 'skipped' (user wasn't tracking inside DebtOS for
   * this period) and auto-create the next month using the CURRENT REGISTRY as
   * the source of truth — not derived ending balances. This is the right
   * action for mid-stream adopters whose registry is up to date but whose
   * past-month allocations inside DebtOS would be fictional.
   *
   * Refuses on closed/already-skipped entries — those are immutable.
   */
  skipCurrentMonth: (debts: Debt[]) => Promise<void>

  // Escape hatch: reopen a closed OR skipped month back to planning state
  reopenMonth: (month: string) => Promise<void>

  /**
   * Add an allocation row for a newly-created debt to the active PLANNING
   * entry. Called from `debtStore.addDebt` so a debt added via the registry
   * dialog appears immediately on the Cash Flow page without requiring a
   * "Reset from Registry" (which would wipe per-month customizations on
   * existing allocations). No-op when the active entry is closed/skipped
   * or when an allocation for this debt already exists.
   */
  addAllocationForNewDebt: (debt: Debt) => Promise<void>

  /**
   * Drop the allocation for a deleted debt from every PLANNING entry.
   * Symmetric with the v0.7.13 orphan-income cleanup. Closed/skipped
   * entries keep their historical row (the deleted debt's allocation is
   * part of that month's frozen truth).
   */
  removeAllocationForDebt: (debtId: string) => Promise<void>

  /** See `debtStore.reset` — used by `bindReposToSession`. */
  reset: () => void
}

/**
 * Helper that locates the active entry by month and runs a mutation function
 * over a draft copy. Persists to repository and updates state in one go.
 */
async function mutateActive(
  get: () => LedgerState,
  set: (fn: (s: LedgerState) => void) => void,
  mutator: (entry: MonthlyLedgerEntry) => MonthlyLedgerEntry
): Promise<void> {
  const { activeMonth, ledgerEntries } = get()
  const idx = ledgerEntries.findIndex((e) => e.month === activeMonth)
  if (idx < 0) return
  const current = ledgerEntries[idx]
  // Both 'closed' and 'skipped' are immutable — only planning entries accept edits.
  if (!current || current.status !== 'planning') return
  const updated = mutator(current)
  // v0.8.34: OPTIMISTIC update. Apply to in-memory state FIRST so the UI
  // reflects the edit instantly, then persist in the background. Pre-fix this
  // awaited the cloud upsert before set(), so for signed-in users every
  // keystroke blocked on a network round-trip before the controlled input
  // could change — and a slow/failed write silently dropped the edit (the
  // v0.8.31 "upsert threw, set() never ran" failure mode, surfacing here in
  // the editing path). On failure we KEEP the edit visible and raise
  // `writeError` so it isn't silent.
  set((s) => {
    s.ledgerEntries[idx] = updated
    s.writeError = null
  })
  try {
    // v0.8.56 — only the changed rows/fields are written (ledgerDiff.ts).
    await getLedgerRepository().saveLedgerChanges(current, updated)
  } catch (err) {
    set((s) => {
      s.writeError =
        err instanceof Error ? err.message : 'Could not save your last change to the cloud.'
    })
  }
}

type LedgerGet = () => LedgerState & LedgerActions
type LedgerSet = (fn: (s: LedgerState) => void) => void

/** Flipped off for the session if the cloud project lacks migration 007. */
let paymentRpcAvailable = true

function currentActual(state: LedgerState, debtId: string): number | undefined {
  const entry = state.ledgerEntries.find((e) => e.month === state.activeMonth)
  return entry?.allocations.find((a) => a.debtId === debtId)?.actualPayment
}

function setActualLocally(
  set: LedgerSet,
  month: string,
  debtId: string,
  amount: number | undefined
) {
  set((s) => {
    const entry = s.ledgerEntries.find((e) => e.month === month)
    const alloc = entry?.allocations.find((a) => a.debtId === debtId)
    if (alloc) alloc.actualPayment = amount
  })
}

/**
 * v0.8.56 — optimistic + atomic payment save. Returns null when the RPC
 * isn't deployed (caller falls back to the legacy path, with optimistic
 * state already rolled back).
 */
async function logPaymentAtomically(
  get: LedgerGet,
  set: LedgerSet,
  debtId: string,
  amount: number | undefined,
  notifyConflict: boolean
): Promise<PaymentOutcome | null> {
  const state = get()
  const entry = state.ledgerEntries.find((e) => e.month === state.activeMonth)
  if (!entry || entry.status !== 'planning') return { status: 'error' }
  const alloc = entry.allocations.find((a) => a.debtId === debtId)
  if (!alloc) return { status: 'error' }

  const debtStore = useDebtStore.getState()
  const debt = debtStore.debts.find((d) => d.id === debtId)
  const previous = alloc.actualPayment
  const balanceBefore = debt?.balance
  const delta = (amount ?? 0) - (previous ?? 0)

  // Optimistic: the screen moves now; the server's answer replaces it.
  setActualLocally(set, entry.month, debtId, amount)
  if (balanceBefore !== undefined) {
    debtStore.applyRemoteBalance(debtId, Math.max(0, balanceBefore - delta))
  }
  const rollBack = () => {
    setActualLocally(set, entry.month, debtId, previous)
    if (balanceBefore !== undefined)
      useDebtStore.getState().applyRemoteBalance(debtId, balanceBefore)
  }

  try {
    const result = await getLedgerRepository().logPayment!(
      entry.id,
      debtId,
      previous ?? null,
      amount ?? null
    )
    setActualLocally(set, entry.month, debtId, result.actualPayment ?? undefined)
    useDebtStore.getState().applyRemoteBalance(debtId, result.balance)
    if (result.status === 'conflict') {
      if (notifyConflict) {
        const name = debt?.name ?? 'this debt'
        const now = result.actualPayment ?? 0
        set((s) => {
          s.syncNotice = `The payment on ${name} was changed on another device and now shows ${formatCurrencyExact(now)}. Your change wasn't saved — enter it again if it's still needed.`
        })
      }
      return { status: 'conflict', actualPayment: result.actualPayment ?? undefined }
    }
    set((s) => {
      s.writeError = null
    })
    return { status: 'ok', actualPayment: result.actualPayment ?? undefined }
  } catch (err) {
    rollBack()
    if (err instanceof PaymentRpcUnavailableError) {
      paymentRpcAvailable = false
      return null
    }
    set((s) => {
      s.writeError = `${PAYMENT_NOT_SAVED}: ${err instanceof Error ? err.message : 'network error'}`
    })
    return { status: 'error' }
  }
}

/**
 * Pre-v0.8.56 payment save: ledger edit, then registry balance by delta.
 * Still used by local repos (one device) and clouds without migration 007.
 */
async function legacySetActualPayment(
  get: LedgerGet,
  set: LedgerSet,
  debtId: string,
  amount: number | undefined
): Promise<PaymentOutcome> {
  // v0.8.49 — live registry sync.
  //
  // Pre-v0.8.49, `setActualPayment` only touched the ledger allocation.
  // The registry's `debt.balance` was invisible to the tracker until Close
  // Month wrote the reconciled ending balance back. That meant a user
  // typing "I paid $500 on Chase" into any Cash Flow surface saw NO
  // change on the Debt Registry page, which drove the founder to bypass
  // the tracker entirely and hand-edit registry balances after every
  // payment. Now the registry moves in step with the log — the delta
  // between the previous and new logged amount is subtracted from
  // `debt.balance` immediately. Close Month still runs the interest
  // math against `alloc.startingBalance` (snapshot) and overwrites
  // registry with the final end-of-month value, so interest is snapped
  // in at close-time as before.
  //
  // Not stamped mid-month: `paidOffAt`. Auto-stamp is close-month only —
  // mid-month can be reversed and the Registry's manual "Mark Paid Off"
  // button (v0.8.41) is the right affordance if the user wants to
  // assert paid-off state before month close.
  const state = get()
  const idx = state.ledgerEntries.findIndex((e) => e.month === state.activeMonth)
  if (idx < 0) return { status: 'error' }
  const current = state.ledgerEntries[idx]
  if (!current || current.status !== 'planning') return { status: 'error' }
  const alloc = current.allocations.find((a) => a.debtId === debtId)
  const previousActual = alloc?.actualPayment ?? 0
  const nextActual = amount ?? 0
  const delta = nextActual - previousActual

  // Apply the ledger edit first (optimistic + writeError handled by
  // mutateActive).
  await mutateActive(get, set, (entry) => {
    const allocations = entry.allocations.map((a) =>
      a.debtId === debtId ? { ...a, actualPayment: amount } : a
    )
    return { ...entry, allocations }
  })

  // Then sync the registry balance by the delta. Best-effort — a
  // registry write failure here doesn't undo the ledger edit; the next
  // close-month writeback will reconcile from `alloc.startingBalance`
  // regardless of registry drift.
  if (Math.abs(delta) < 0.0001) return { status: 'ok', actualPayment: amount }
  const debtStore = useDebtStore.getState()
  const debt = debtStore.debts.find((d) => d.id === debtId)
  if (!debt) return { status: 'ok', actualPayment: amount }
  const newBalance = Math.max(0, debt.balance - delta)
  if (Math.abs(newBalance - debt.balance) < PAID_OFF_TOLERANCE) {
    return { status: 'ok', actualPayment: amount }
  }
  try {
    await debtStore.updateDebt(debtId, { balance: newBalance })
  } catch (err) {
    console.warn(
      `[DebtOS] setActualPayment registry sync failed for debt ${debtId}:`,
      err instanceof Error ? err.message : err
    )
  }
  return { status: 'ok', actualPayment: amount }
}

/** Drops income rows whose source was deleted (planning months only; v0.7.13). */
function sweepOrphanIncome(
  entries: MonthlyLedgerEntry[],
  incomeSources: IncomeSource[]
): MonthlyLedgerEntry[] {
  const ids = new Set(incomeSources.map((src) => src.id))
  return entries.map((entry) =>
    entry.status === 'planning' && entry.income.some((i) => !ids.has(i.sourceId))
      ? { ...entry, income: entry.income.filter((i) => ids.has(i.sourceId)) }
      : entry
  )
}

export const useLedgerStore = create<LedgerState & LedgerActions>()(
  immer((set, get) => ({
    incomeSources: [],
    ledgerEntries: [],
    activeMonth: currentMonth(),
    isLoading: false,
    isInitialized: false,
    initError: null,
    writeError: null,
    syncNotice: null,

    initialize: async (debts) => {
      if (get().isInitialized) return
      set((s) => {
        s.isLoading = true
        s.initError = null
      })
      try {
        const repo = getLedgerRepository()
        // v0.8.32 — 15-second timeout on the initial reads. Pre-fix, a
        // hung Supabase round-trip (RLS quirk, Cloudflare blip, network
        // drop) left the user stuck on "Loading cash flow..." forever
        // because the await never resolved or rejected — `isInitialized`
        // stayed false and `initError` was never set. Treating timeout as
        // "throw" routes through the outer catch and surfaces a real
        // error block with the retry path.
        type InitFetchResult =
          | { _timedOut: false; value: [IncomeSource[], MonthlyLedgerEntry[]] }
          | { _timedOut: true }
        const initSentinel: InitFetchResult = { _timedOut: true }
        const result = await withTimeout<InitFetchResult>(
          Promise.all([repo.getAllIncomeSources(), repo.getAllLedgerEntries()]).then((r) => ({
            _timedOut: false,
            value: r,
          })),
          15_000,
          initSentinel
        )
        if (result._timedOut) {
          throw new Error(
            'Loading cash flow took longer than 15 seconds. Check your network connection and try reloading. If this keeps happening, the Supabase auth / RLS policies may need attention.'
          )
        }
        const [incomeSources, ledgerEntries] = result.value

        // Orphan-income sweep (v0.7.13). Pre-v0.7.13 deleting an income
        // source removed it from `incomeSources` but left the matching
        // `{ sourceId, amount }` entry in every planning ledger entry's
        // `income` array — silently inflating `totalIncome`. We filter on
        // every init: for each PLANNING entry, drop any income entry
        // whose sourceId no longer maps to a real source. Closed and
        // skipped entries are immutable historical records — leave them
        // alone, the deleted source's amount is part of that month's truth.
        //
        // v0.8.32 — apply the filter IN-MEMORY only; do NOT persist to the
        // repo during init. Pre-fix the per-entry `await repo.upsertLedgerEntry`
        // was load-bearing: a single cloud upsert hang (or pre-v0.8.31
        // "column doesn't exist" rejection) blocked the whole init promise,
        // and "Loading cash flow..." never resolved. The next real edit on
        // a cleaned entry will persist the cleaned state naturally; until
        // then the in-memory state is correct and the UI is honest. The
        // orphan reference on disk is benign — the filter runs on every
        // init so display stays clean.
        const sourceIds = new Set(incomeSources.map((s) => s.id))
        let entries = ledgerEntries
        const cleaned: MonthlyLedgerEntry[] = []
        let sweepedAny = false
        for (const entry of entries) {
          if (entry.status !== 'planning') {
            cleaned.push(entry)
            continue
          }
          const orphans = entry.income.filter((i) => !sourceIds.has(i.sourceId))
          if (orphans.length === 0) {
            cleaned.push(entry)
            continue
          }
          sweepedAny = true
          const fixed: MonthlyLedgerEntry = {
            ...entry,
            income: entry.income.filter((i) => sourceIds.has(i.sourceId)),
          }
          cleaned.push(fixed)
        }
        if (sweepedAny) {
          console.info(
            '[DebtOS] Cleaned orphan income entries on planning months — see v0.7.13 CHANGELOG.'
          )
          entries = cleaned
        }

        // Bootstrap: if no entries exist, create one for the current month
        const today = currentMonth()
        if (!entries.find((e) => e.month === today)) {
          const blank = buildBlankLedgerEntry(today, debts, incomeSources)
          await repo.upsertLedgerEntry(blank)
          entries = [...entries, blank].sort((a, b) => a.month.localeCompare(b.month))
        }

        // Restore the user's previously-selected active month if persisted
        // and still valid (entry exists). Otherwise default to today's month
        // — the bootstrap above guarantees an entry exists for today.
        const persisted = readPersistedActiveMonth()
        const activeMonth =
          persisted && entries.find((e) => e.month === persisted) ? persisted : today

        set((s) => {
          s.incomeSources = incomeSources
          s.ledgerEntries = entries
          s.activeMonth = activeMonth
          s.isLoading = false
          s.isInitialized = true
        })

        // Refresh persistence so a stale or fallback value gets re-anchored.
        writePersistedActiveMonth(activeMonth)
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        // Surface the error so a stuck "Loading..." doesn't hide a real bug
        console.error('[DebtOS] Ledger init failed:', err)
        set((s) => {
          s.isLoading = false
          s.initError = message
        })
      }
    },

    // ─── Income source CRUD ──────────────────────────────────────────
    addIncomeSource: async (sourceData) => {
      const newSource: IncomeSource = {
        ...sourceData,
        id: safeUUID(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      const repo = getLedgerRepository()
      await repo.upsertIncomeSource(newSource)

      // Auto-attach the source's default amount to the active planning month so
      // the total income updates immediately (otherwise the user has to click
      // into the per-row input before the total reflects anything — confusing).
      const { activeMonth, ledgerEntries } = get()
      const activeIdx = ledgerEntries.findIndex((e) => e.month === activeMonth)
      let updatedEntry: MonthlyLedgerEntry | null = null
      if (activeIdx >= 0 && newSource.isActive && newSource.defaultAmount) {
        const current = ledgerEntries[activeIdx]
        if (current && current.status === 'planning') {
          updatedEntry = {
            ...current,
            income: [
              ...current.income,
              { sourceId: newSource.id, amount: newSource.defaultAmount },
            ],
          }
          await repo.saveLedgerChanges(current, updatedEntry)
        }
      }

      set((s) => {
        s.incomeSources.push(newSource)
        if (updatedEntry && activeIdx >= 0) {
          s.ledgerEntries[activeIdx] = updatedEntry
        }
      })
    },

    updateIncomeSource: async (id, patch) => {
      const existing = get().incomeSources.find((s) => s.id === id)
      if (!existing) return
      const updated: IncomeSource = {
        ...existing,
        ...patch,
        updatedAt: new Date().toISOString(),
      }

      // v0.8.34: when the edit dialog changes the source's amount, ALSO apply
      // it to the active PLANNING month's income so the planning figure moves
      // immediately (the founder's chosen behavior). Only when the amount
      // actually changed — editing just the name/type/stability must not
      // clobber a per-month amount the user set inline. Closed/skipped months
      // are immutable and left untouched.
      const amountChanged =
        patch.defaultAmount !== undefined && patch.defaultAmount !== existing.defaultAmount
      const { activeMonth, ledgerEntries } = get()
      const activeIdx = ledgerEntries.findIndex((e) => e.month === activeMonth)
      let updatedEntry: MonthlyLedgerEntry | null = null
      let entryBefore: MonthlyLedgerEntry | null = null
      if (amountChanged && activeIdx >= 0) {
        const current = ledgerEntries[activeIdx]
        if (current && current.status === 'planning') {
          const newMonthAmount = updated.defaultAmount ?? 0
          const incomeIdx = current.income.findIndex((i) => i.sourceId === id)
          const income = [...current.income]
          const row = incomeIdx >= 0 ? income[incomeIdx] : undefined
          if (row) income[incomeIdx] = { ...row, amount: newMonthAmount }
          else income.push({ sourceId: id, amount: newMonthAmount })
          updatedEntry = { ...current, income }
          entryBefore = current
        }
      }

      // v0.8.48 — OPTIMISTIC update. Apply to in-memory state FIRST so
      // the UI reflects the edit instantly, then persist in the
      // background. Pre-fix this awaited both cloud upserts before
      // set() — same anti-pattern v0.8.34 fixed on `mutateActive` for
      // the inline path, but the dialog path was missed. On a slow or
      // failing cloud write the dialog Save appeared frozen; on a
      // rejection the edit was silently dropped. Failure now raises
      // `writeError` (same as mutateActive) so it isn't silent.
      set((s) => {
        const idx = s.incomeSources.findIndex((src) => src.id === id)
        if (idx >= 0) s.incomeSources[idx] = updated
        if (updatedEntry && activeIdx >= 0) s.ledgerEntries[activeIdx] = updatedEntry
        s.writeError = null
      })
      try {
        const repo = getLedgerRepository()
        await repo.upsertIncomeSource(updated)
        if (updatedEntry && entryBefore) await repo.saveLedgerChanges(entryBefore, updatedEntry)
      } catch (err) {
        set((s) => {
          s.writeError =
            err instanceof Error ? err.message : 'Could not save the income change to the cloud.'
        })
      }
    },

    removeIncomeSource: async (id) => {
      // Two-step cleanup: drop the source itself, AND strip its entries from
      // every PLANNING ledger entry's `income` array. Without the second
      // step, the orphan `{ sourceId: deletedId, amount: X }` remains in the
      // entry — invisible in the IncomeSourcesPanel (which iterates
      // `incomeSources`, not `entry.income`) but silently inflating
      // `totalIncome`. Closed and skipped entries are intentionally left
      // alone — they're immutable historical records and the deleted source's
      // amount is part of that month's truth.
      const repo = getLedgerRepository()
      await repo.deleteIncomeSource(id)

      const { ledgerEntries } = get()
      const updatedEntries: MonthlyLedgerEntry[] = []
      for (const entry of ledgerEntries) {
        const hasOrphan = entry.income.some((i) => i.sourceId === id)
        if (entry.status !== 'planning' || !hasOrphan) {
          updatedEntries.push(entry)
          continue
        }
        const cleaned: MonthlyLedgerEntry = {
          ...entry,
          income: entry.income.filter((i) => i.sourceId !== id),
        }
        await repo.saveLedgerChanges(entry, cleaned)
        updatedEntries.push(cleaned)
      }

      set((s) => {
        s.incomeSources = s.incomeSources.filter((src) => src.id !== id)
        s.ledgerEntries = updatedEntries
      })
    },

    // ─── Navigation ──────────────────────────────────────────────────
    setActiveMonth: (month) => {
      set((s) => {
        s.activeMonth = month
      })
      // Persist immediately so the next page load lands here. Failures are
      // silent — see writePersistedActiveMonth.
      writePersistedActiveMonth(month)
    },

    clearWriteError: () => {
      set((s) => {
        s.writeError = null
      })
    },

    // ─── Editing the active entry ────────────────────────────────────
    setIncomeAmount: async (sourceId, amount) => {
      await mutateActive(get, set, (entry) => {
        const idx = entry.income.findIndex((i) => i.sourceId === sourceId)
        const income = [...entry.income]
        const existing = idx >= 0 ? income[idx] : undefined
        if (existing) {
          income[idx] = { ...existing, amount }
        } else {
          income.push({ sourceId, amount })
        }
        return { ...entry, income }
      })
    },

    setMonthMinimumPayment: async (debtId, amount) => {
      // Writes the override; resolveAllocation honours it during planning.
      await mutateActive(get, set, (entry) => {
        const allocations = entry.allocations.map((a) =>
          a.debtId === debtId ? { ...a, monthMinimumPaymentOverride: amount } : a
        )
        return { ...entry, allocations }
      })
    },

    clearMonthMinimumPaymentOverride: async (debtId) => {
      await mutateActive(get, set, (entry) => {
        const allocations = entry.allocations.map((a) =>
          a.debtId === debtId ? { ...a, monthMinimumPaymentOverride: undefined } : a
        )
        return { ...entry, allocations }
      })
    },

    setPlannedPayment: async (debtId, amount) => {
      await mutateActive(get, set, (entry) => {
        const allocations = entry.allocations.map((a) =>
          a.debtId === debtId ? { ...a, plannedPayment: amount } : a
        )
        return { ...entry, allocations }
      })
    },

    setActualPayment: async (debtId, amount) => {
      // v0.8.56 — cloud saves go through the atomic `log_payment` RPC
      // (migration 007): actual payment + balance delta in one transaction,
      // refused if another device changed this payment since we loaded it.
      // Local repos (and clouds without migration 007) use the legacy path.
      if (paymentRpcAvailable && getLedgerRepository().logPayment) {
        const outcome = await logPaymentAtomically(get, set, debtId, amount, true)
        if (outcome) return outcome
      }
      return legacySetActualPayment(get, set, debtId, amount)
    },

    addPayment: async (debtId, amount) => {
      const before = currentActual(get(), debtId)
      const { setActualPayment } = get()
      const first = await setActualPayment(debtId, (before ?? 0) + amount)
      if (first.status !== 'conflict') return first
      // Another device logged a payment on this debt first. "I paid $X more"
      // is still true on top of whatever is stored now, so retry once.
      set((s) => {
        s.syncNotice = null
      })
      const fresh = first.actualPayment ?? 0
      const second = await setActualPayment(debtId, fresh + amount)
      if (second.status === 'ok') {
        const name = useDebtStore.getState().debts.find((d) => d.id === debtId)?.name ?? 'this debt'
        set((s) => {
          s.syncNotice = `A payment on ${name} had already been logged on another device — yours was added on top. Total logged this month: ${formatCurrencyExact(second.actualPayment ?? 0)}.`
        })
      }
      return second
    },

    clearSyncNotice: () => {
      set((s) => {
        s.syncNotice = null
      })
    },

    refresh: async () => {
      const state = get()
      if (!state.isInitialized || state.writeError || hasPendingWrites()) return
      const startEpoch = writeEpoch()
      try {
        const repo = getLedgerRepository()
        const [incomeSources, ledgerEntries] = await Promise.all([
          repo.getAllIncomeSources(),
          repo.getAllLedgerEntries(),
        ])
        if (writeEpoch() !== startEpoch || hasPendingWrites()) return
        const entries = sweepOrphanIncome(ledgerEntries, incomeSources)
        set((s) => {
          s.incomeSources = incomeSources
          s.ledgerEntries = entries
          if (!entries.some((e) => e.month === s.activeMonth)) {
            s.activeMonth = entries.at(-1)?.month ?? s.activeMonth
          }
        })
      } catch {
        // Background refresh: the next focus retries; the data on screen stays.
      }
    },

    setEndingBalanceOverride: async (debtId, amount) => {
      await mutateActive(get, set, (entry) => {
        const allocations = entry.allocations.map((a) =>
          a.debtId === debtId ? { ...a, endingBalanceOverride: amount } : a
        )
        return { ...entry, allocations }
      })
    },

    // ─── Quick-fill ──────────────────────────────────────────────────
    applyMinimums: async (debts) => {
      await mutateActive(get, set, (entry) => applyMinimumsOnly(entry, debts))
    },

    applyAvalancheSurplus: async (debts, surplus) => {
      await mutateActive(get, set, (entry) =>
        applyStrategySurplus(entry, debts, surplus, 'avalanche')
      )
    },

    applySnowballSurplus: async (debts, surplus) => {
      await mutateActive(get, set, (entry) =>
        applyStrategySurplus(entry, debts, surplus, 'snowball')
      )
    },

    // ─── Reset month from registry ───────────────────────────────────
    resetMonthFromRegistry: async (debts) => {
      const { activeMonth, ledgerEntries, incomeSources } = get()
      const idx = ledgerEntries.findIndex((e) => e.month === activeMonth)
      if (idx < 0) return
      const current = ledgerEntries[idx]
      // Refuse on closed/skipped months — both are immutable.
      if (!current || current.status !== 'planning') return

      // Build a fresh blank for this month from current registry state, then
      // splice in the user's existing income amounts + notes. Essentials are
      // derived live from the recurring-expenses store on every read, so
      // there's nothing essentials-shaped to preserve here.
      const blank = buildBlankLedgerEntry(activeMonth, debts, incomeSources)
      const reset: MonthlyLedgerEntry = {
        ...blank,
        // preserve identifiers and timestamps
        id: current.id,
        createdAt: current.createdAt,
        // preserve user-entered cash-flow values that aren't allocation-shaped
        income: current.income.length > 0 ? current.income : blank.income,
        notes: current.notes,
      }

      await getLedgerRepository().upsertLedgerEntry(reset)
      set((s) => {
        s.ledgerEntries[idx] = reset
      })
    },

    // ─── Month lifecycle ─────────────────────────────────────────────
    closeCurrentMonth: async (debts, essentials) => {
      const { activeMonth, ledgerEntries } = get()
      const idx = ledgerEntries.findIndex((e) => e.month === activeMonth)
      if (idx < 0) return
      const current = ledgerEntries[idx]
      // Refuse on closed/skipped — both are immutable.
      if (!current || current.status !== 'planning') return

      const closed = closeMonthEntry(current, debts, essentials)
      const repo = getLedgerRepository()
      await repo.upsertLedgerEntry(closed)

      // v0.8.23: write the ending balance for each allocation back to
      // `debt.balance` in the registry. Pre-fix, closing a month was
      // invisible to the registry — Debt Registry, Strategy Engine, Path
      // to Freedom, and the next planning month all kept reading the old
      // pre-payment balance. The snapshot for reopen (`balanceBeforeClose`)
      // was just stamped on each allocation by `closeMonthEntry`, so we can
      // restore on reopen symmetrically. Best-effort: failures on a single
      // debt are logged but don't abort the close — partial sync still
      // beats none, and the closed entry has already been persisted above.
      const debtStore = useDebtStore.getState()
      for (const alloc of closed.allocations) {
        const newBalance = alloc.endingBalanceOverride ?? alloc.endingBalanceCalculated ?? 0
        const existing = debtStore.debts.find((d) => d.id === alloc.debtId)
        if (!existing) continue // debt deleted between planning and close
        if (Math.abs(existing.balance - newBalance) < PAID_OFF_TOLERANCE) continue // no change
        // v0.8.41 — auto-stamp `paidOffAt` when this close drives the
        // balance to zero (or below PAID_OFF_TOLERANCE) and the debt
        // doesn't already have a stamp. Manual markPaidOff stamps on
        // its own write; legacy zero-balance debts cleared pre-v0.8.41
        // keep their NULL stamp and still display as cleared via the
        // `balance == 0` fallback predicate.
        const becomesPaidOff = newBalance < PAID_OFF_TOLERANCE && existing.paidOffAt === undefined
        const patch: Parameters<typeof debtStore.updateDebt>[1] = becomesPaidOff
          ? { balance: newBalance, paidOffAt: new Date().toISOString() }
          : { balance: newBalance }
        try {
          await debtStore.updateDebt(alloc.debtId, patch)
        } catch (err) {
          // Log but don't abort — closed entry is already persisted, and the
          // next planning month will read whatever balance the registry has.
          console.warn(
            `[DebtOS] Close-month write-back failed for debt ${alloc.debtId}:`,
            err instanceof Error ? err.message : err
          )
        }
      }

      // Auto-create next month if it doesn't already exist (Option B).
      // Re-read debts after the registry write-back above so
      // `buildNextMonthFromPrevious` projects from the updated registry.
      // Note: `next` already inherits closed.endingBalance via its own
      // pathway (status === 'closed' branch in buildNextMonthFromPrevious),
      // but a stale `debts` reference would corrupt other derivations
      // (effective APR, minimum payment) on the new month's snapshot.
      const freshDebts = useDebtStore.getState().debts
      const nextMonth = closed.month
      const allEntries = [...ledgerEntries]
      allEntries[idx] = closed

      const next = buildNextMonthFromPrevious(closed, freshDebts)
      const existsAlready = allEntries.find((e) => e.month === next.month)
      if (!existsAlready) {
        await repo.upsertLedgerEntry(next)
        allEntries.push(next)
        allEntries.sort((a, b) => a.month.localeCompare(b.month))
      }

      const advancedTo = existsAlready ? existsAlready.month : next.month
      set((s) => {
        s.ledgerEntries = allEntries
        // Auto-advance to the new month
        s.activeMonth = advancedTo
      })
      writePersistedActiveMonth(advancedTo)

      // Use nextMonth to silence unused warning if needed
      void nextMonth
    },

    startNextMonth: async (debts) => {
      // Manual trigger (Option C) — create the month after the active one
      // even if the active one isn't closed yet
      const { activeMonth, ledgerEntries } = get()
      const current = ledgerEntries.find((e) => e.month === activeMonth)
      if (!current) return

      const next = buildNextMonthFromPrevious(current, debts)
      // Don't overwrite if it already exists
      const exists = ledgerEntries.find((e) => e.month === next.month)
      if (exists) {
        set((s) => {
          s.activeMonth = exists.month
        })
        writePersistedActiveMonth(exists.month)
        return
      }

      await getLedgerRepository().upsertLedgerEntry(next)
      set((s) => {
        s.ledgerEntries.push(next)
        s.ledgerEntries.sort((a, b) => a.month.localeCompare(b.month))
        s.activeMonth = next.month
      })
      writePersistedActiveMonth(next.month)
    },

    // ─── Skip month ──────────────────────────────────────────────────
    /**
     * Mark the active month as 'skipped' (user wasn't actively tracking
     * inside DebtOS for this period — the registry has been kept current
     * outside the app) and advance to the next month using the CURRENT
     * REGISTRY as the source of truth, not derived ending balances.
     *
     * Differs from `closeCurrentMonth` in two important ways:
     *   1. Status becomes 'skipped' (not 'closed') so trajectory math
     *      naturally excludes this month from the historical record.
     *   2. Next month is built via `buildBlankLedgerEntry(...)` which uses
     *      the current registry — `closeCurrentMonth` uses
     *      `buildNextMonthFromPrevious(...)` which projects from the
     *      previous month's reconciled allocations. Skipping means the
     *      previous allocations are fictional, so ignore them.
     *
     * Refuses on already-immutable entries.
     */
    skipCurrentMonth: async (debts) => {
      const { activeMonth, ledgerEntries, incomeSources } = get()
      const idx = ledgerEntries.findIndex((e) => e.month === activeMonth)
      if (idx < 0) return
      const current = ledgerEntries[idx]
      if (!current || current.status !== 'planning') return

      const skipped: MonthlyLedgerEntry = {
        ...current,
        status: 'skipped',
        // Reuse closedAt as the timestamp of the lifecycle transition. The
        // field semantically captures "moment this entry became immutable"
        // regardless of which terminal status it landed in.
        closedAt: new Date().toISOString(),
      }

      // Build next month directly from registry — the whole point of skip.
      const nextMonth = addMonths(activeMonth, 1)
      const blank = buildBlankLedgerEntry(nextMonth, debts, incomeSources)

      const repo = getLedgerRepository()
      await repo.upsertLedgerEntry(skipped)

      const allEntries = [...ledgerEntries]
      allEntries[idx] = skipped

      const existsAlready = allEntries.find((e) => e.month === blank.month)
      if (!existsAlready) {
        await repo.upsertLedgerEntry(blank)
        allEntries.push(blank)
        allEntries.sort((a, b) => a.month.localeCompare(b.month))
      }

      const advancedTo = existsAlready ? existsAlready.month : blank.month
      set((s) => {
        s.ledgerEntries = allEntries
        s.activeMonth = advancedTo
      })
      writePersistedActiveMonth(advancedTo)
    },

    reopenMonth: async (month) => {
      const { ledgerEntries } = get()
      const idx = ledgerEntries.findIndex((e) => e.month === month)
      if (idx < 0) return
      const entry = ledgerEntries[idx]
      // Either 'closed' or 'skipped' can be reopened back to planning.
      // Refuse on already-planning (no-op) and on missing entries.
      if (!entry || entry.status === 'planning') return

      // v0.8.23: roll the registry back to each allocation's `balanceBeforeClose`
      // — the symmetric counterpart to the close-time write-back. Only applied
      // when this is the LATEST closed entry. If a more recent closed entry
      // exists, its snapshots assert a starting balance that depends on this
      // entry's ending balance staying put; rolling back here would silently
      // make those snapshots inconsistent with the registry. In that case we
      // leave the registry alone and let the user re-close in sequence if
      // they want a different outcome.
      //
      // Skipped entries also skip the restore: skipping doesn't write to the
      // registry, so there's nothing to roll back. balanceBeforeClose only
      // gets stamped on closed allocations anyway.
      if (entry.status === 'closed') {
        const latestClosed = [...ledgerEntries]
          .filter((e) => e.status === 'closed')
          .sort((a, b) => b.month.localeCompare(a.month))[0]
        if (latestClosed && latestClosed.month === entry.month) {
          const debtStore = useDebtStore.getState()
          for (const alloc of entry.allocations) {
            if (alloc.balanceBeforeClose === undefined) continue
            const existing = debtStore.debts.find((d) => d.id === alloc.debtId)
            if (!existing) continue
            // v0.8.49 — restore `paidOffAt` alongside `balance` for full
            // reopen symmetry. Tri-state on the snapshot:
            //   string    → restore that timestamp (set on the patch)
            //   null      → clear (call unmarkPaidOff — the only path that
            //               correctly drops the column so cloud writes null)
            //   undefined → pre-v0.8.49 close, snapshot not captured;
            //               skip the paidOffAt half of the restore
            try {
              const patch: Parameters<typeof debtStore.updateDebt>[1] = {
                balance: alloc.balanceBeforeClose,
              }
              if (typeof alloc.paidOffAtBeforeClose === 'string') {
                patch.paidOffAt = alloc.paidOffAtBeforeClose
              }
              const balanceUnchanged =
                Math.abs(existing.balance - alloc.balanceBeforeClose) < PAID_OFF_TOLERANCE
              const paidOffUnchanged =
                alloc.paidOffAtBeforeClose === undefined ||
                (typeof alloc.paidOffAtBeforeClose === 'string' &&
                  existing.paidOffAt === alloc.paidOffAtBeforeClose) ||
                (alloc.paidOffAtBeforeClose === null && existing.paidOffAt === undefined)
              if (!balanceUnchanged || !paidOffUnchanged) {
                await debtStore.updateDebt(alloc.debtId, patch)
              }
              // Snapshot said unstamped and registry currently has a stamp —
              // clear it via unmarkPaidOff (the only action that drops the
              // field so cloud writes null instead of leaving prior value).
              if (alloc.paidOffAtBeforeClose === null && existing.paidOffAt !== undefined) {
                await debtStore.unmarkPaidOff(alloc.debtId)
              }
            } catch (err) {
              console.warn(
                `[DebtOS] Reopen-month registry restore failed for debt ${alloc.debtId}:`,
                err instanceof Error ? err.message : err
              )
            }
          }
        }
      }

      const reopened: MonthlyLedgerEntry = {
        ...entry,
        status: 'planning',
        closedAt: undefined,
      }
      await getLedgerRepository().upsertLedgerEntry(reopened)
      set((s) => {
        s.ledgerEntries[idx] = reopened
      })
    },

    addAllocationForNewDebt: async (debt) => {
      // Only the active planning entry gets the new row. Closed / skipped
      // entries are immutable historical records.
      if (debt.balance <= 0) return
      const { activeMonth, ledgerEntries } = get()
      const idx = ledgerEntries.findIndex((e) => e.month === activeMonth)
      if (idx < 0) return
      const current = ledgerEntries[idx]
      if (!current || current.status !== 'planning') return
      // Skip if there's already an allocation for this debt (defensive)
      if (current.allocations.some((a) => a.debtId === debt.id)) return

      // v0.8.44 — parseLocalMonth restores local-time anchoring.
      const monthStartDate = parseLocalMonth(activeMonth)
      const updated: MonthlyLedgerEntry = {
        ...current,
        allocations: [
          ...current.allocations,
          {
            debtId: debt.id,
            startingBalance: debt.balance,
            effectiveAPR: effectiveAPR(debt, 0, monthStartDate),
            monthMinimumPayment: debt.minimumPayment,
            plannedPayment: debt.minimumPayment,
          },
        ],
      }
      await getLedgerRepository().saveLedgerChanges(current, updated)
      set((s) => {
        s.ledgerEntries[idx] = updated
      })
    },

    removeAllocationForDebt: async (debtId) => {
      // Strip from every PLANNING entry — symmetric with v0.7.13's
      // income-orphan cleanup. Closed and skipped entries are
      // immutable historical records and keep their row.
      const repo = getLedgerRepository()
      const { ledgerEntries } = get()
      const updatedEntries: MonthlyLedgerEntry[] = []
      for (const entry of ledgerEntries) {
        const hasAlloc = entry.allocations.some((a) => a.debtId === debtId)
        if (entry.status !== 'planning' || !hasAlloc) {
          updatedEntries.push(entry)
          continue
        }
        const cleaned: MonthlyLedgerEntry = {
          ...entry,
          allocations: entry.allocations.filter((a) => a.debtId !== debtId),
        }
        await repo.saveLedgerChanges(entry, cleaned)
        updatedEntries.push(cleaned)
      }
      set((s) => {
        s.ledgerEntries = updatedEntries
      })
    },

    reset: () => {
      // A rebind may bring a repo that has the payment RPC; probe it again.
      paymentRpcAvailable = true
      set((s) => {
        s.writeError = null
        s.syncNotice = null
        s.incomeSources = []
        s.ledgerEntries = []
        // Reset activeMonth too. Without this, the previous user's month
        // selection sticks across a sign-out → sign-in flip and a stale
        // activeMonth points at an entry that doesn't exist in the freshly
        // initialised entries list until `initialize` reassigns it.
        s.activeMonth = currentMonth()
        s.isLoading = false
        s.isInitialized = false
        s.initError = null
      })
    },
  }))
)
